import { Feature, FeatureCollection, LineString, Point, Polygon } from 'geojson';
import * as turf from '@turf/helpers';

/**
 * Helper class, contains utility functions for drawing lines and polygons on map.
 * Calculations are done using Turf, http://turfjs.org/
 */
export class Draw {
  /**
   * Datasource for drawing current line or area to map
   */
  private geojson: FeatureCollection<Point | Polygon | LineString>;

  /**
   * This is for creating circles using turf/buffer.
   */
  private bufferPoints: turf.Position[] = [];

  /**
   * Default constructor
   * @param {mapboxgl.Map} map - current map instance
   * @param {string} type - line or area?
   */
  constructor(private map: mapboxgl.Map) {
    this.clear();
  }
  private updatePoints(e: mapboxgl.MapMouseEvent) {
    const point: Feature<Point> = {
      'type': 'Feature',
      'geometry': {
        'type': 'Point',
        'coordinates': [
          e.lngLat.lng,
          e.lngLat.lat
        ]
      },
      'properties': {
        'id': String(new Date().getTime())
      }
    };
    const points = this.geojson.features.filter(f => f.geometry.type === 'Point' );
    points.push(point);
    this.geojson.features = points;
    this.bufferPoints.push([e.lngLat.lng, e.lngLat.lat]);
  }
  /**
   * Reset all datasources and models
   */
  public clear() {
    this.bufferPoints = [];
    this.geojson = {
      'type': 'FeatureCollection',
      'features': []
    };
    this.getSource().setData(this.geojson);
  }
  public polygon(e: mapboxgl.MapMouseEvent): Feature<Polygon> | null {
    this.updatePoints(e);
    if (this.bufferPoints.length > 2) {
      /**Add polygon's last point. Must point to the same location than the first one */
      const updatedPoints = this.bufferPoints.map(p => p).concat(this.bufferPoints.slice(0, 1));
      const polygonFeature = turf.polygon([updatedPoints], {});
      this.geojson.features.push(polygonFeature);
      this.getSource().setData(this.geojson);
      return polygonFeature; //this.geojson.features[0] as Feature<Polygon>;
    }
    this.getSource().setData(this.geojson);
    return null;
  }
  public line(e: mapboxgl.MapMouseEvent): Feature<LineString> | null {
    this.updatePoints(e);
    if (this.bufferPoints.length > 1) {
      const lineStringFeature = turf.lineString(this.bufferPoints);
      this.geojson.features.push(lineStringFeature);
      this.getSource().setData(this.geojson);
      return lineStringFeature;
    }
    this.getSource().setData(this.geojson);
    return null;
  }

  /**
   * Helper function for fetching and initializing datasource for draw functions.
   * @returns {mapboxgl.GeoJSONSource}
   */
  private getSource(): mapboxgl.GeoJSONSource {
    if (!this.map.getSource('draw-layer')) {
      this.map.addSource('draw-layer', {
        type: 'geojson',
        data: {
          'type': 'FeatureCollection',
          'features': []
        }
      });
      this.map.addLayer({
        'id': 'draw-polygon',
        'type': 'fill',
        'source': 'draw-layer',
        'layout': {
          'visibility': 'visible'
        },
        'paint': {
          'fill-color': '#58A618',
          'fill-opacity': 0.2,
        },
        'filter': [
          'in',
          '$type',
          'Polygon'
        ],
        'metadata': {
          'selectable': false
        }
      });
      this.map.addLayer({
        'id': 'draw-points',
        'type': 'circle',
        'source': 'draw-layer',
        'layout': {
          'visibility': 'visible'
        },
        'paint': {
          'circle-radius': 5,
          'circle-color': '#58A618',
          'circle-stroke-width': 1,
          'circle-stroke-color': '#000'
        },
        'filter': [
          'in',
          '$type',
          'Point'
        ],
        'metadata': {
          'selectable': false
        }
      });

      this.map.addLayer({
        'id': 'draw-lines',
        'type': 'line',
        'source': 'draw-layer',
        'layout': {
          'visibility': 'visible',
          'line-cap': 'round',
          'line-join': 'round'
        },
        'paint': {
          'line-color': '#58A618',
          'line-width': 2.5
        },
        'filter': [
          'in',
          '$type',
          'LineString'
        ],
        'metadata': {
          'selectable': false
        }
      });
    }
    return this.map.getSource('draw-layer') as mapboxgl.GeoJSONSource;
  }
}
